import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Send } from "lucide-react";
import { Typography, Box, Button as MuiButton, TextField } from '@mui/material';
import { useThemeContext } from './ThemeWrapper';
import JapaneseKeyboard from './JapaneseKeyboard';
import Sidebar from './Sidebar';

interface Message {
    type: "bot" | "user";
    text: string;
    animate?: boolean;
}

export default function Conversation() {
    const [questions, setQuestions] = useState<any[]>([]);
    const [current, setCurrent] = useState(0);
    const [userInput, setUserInput] = useState("");
    const [messages, setMessages] = useState<Message[]>([]);
    const [status, setStatus] = useState<"ask" | "correct" | "wrong" | "done">("ask");
    const [showSkip, setShowSkip] = useState(false);
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const chatWindowRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { isDarkTheme } = useThemeContext();

    useEffect(() => {
        fetch("/conversationData.json")
            .then((res) => res.json())
            .then((data) => {
                setQuestions(data);
                setMessages([{ type: "bot", text: data[0].question, animate: true }]);
            });
    }, []);

    useEffect(() => {
        if (status === "ask" && inputRef.current) inputRef.current.focus();
    }, [status, current]);

    useEffect(() => {
        if (chatWindowRef.current) {
            chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = () => {
        if (!userInput.trim()) return;
        const answer = questions[current].answer.trim();

        setMessages((msgs) => [...msgs, { type: "user", text: userInput }]);

        if (userInput.trim() === answer) {
            setStatus("correct");
            setMessages((msgs) => [...msgs, { type: "bot", text: "Yay! 🎉 That's correct!", animate: false }]);
            setTimeout(() => {
                goNext();
            }, 1200);
        } else {
            setStatus("wrong");
            setMessages((msgs) => [...msgs, { type: "bot", text: "Oops! Try again or skip? 🤔", animate: false }]);
            setShowSkip(true);
        }
        setUserInput("");
    };

    const goNext = () => {
        setShowSkip(false);
        if (current + 1 < questions.length) {
            setCurrent(current + 1);
            setMessages((msgs) => [...msgs, { type: "bot", text: questions[current + 1].question, animate: true }]);
            setStatus("ask");
        } else {
            setMessages((msgs) => [...msgs, { type: "bot", text: "All done! Great job! 🌸", animate: false }]);
            setStatus("done");
        }
    };

    const handleVoice = () => {
        setVoiceError(null);
        if (!('SpeechRecognition' in window) && !('webkitSpeechRecognition' in window)) {
            setVoiceError("Voice recognition not supported by your browser.");
            return;
        }

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = "ja-JP";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event: any) => {
            const transcript = event.results[0][0].transcript;
            setUserInput(transcript);
        };

        recognition.onerror = (event: any) => {
            setVoiceError(`Voice input error: ${event.error}`);
        };

        recognition.start();
    };

    const handleKeyboardKeyPress = (char: string) => setUserInput((prev) => prev + char);
    const handleKeyboardBackspace = () => setUserInput((prev) => prev.slice(0, -1));
    const handleKeyboardEnter = () => handleSend();

    return (
        <div className={`flex min-h-screen font-inter ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
            <Sidebar />

            <main className={`flex-1 p-4 sm:p-8 pb-20 md:p-12 overflow-y-auto ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
                <div className="flex flex-col md:flex-row md:justify-center md:items-start gap-6 w-full max-w-4xl mx-auto h-full">
                    <motion.div
                        className={`w-full max-w-md mx-auto md:mx-0 rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col min-h-[70vh] md:min-h-[480px] relative overflow-hidden pt-4 ${isDarkTheme ? 'bg-zinc-900 text-white' : 'bg-white text-black'}`}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ type: "spring", stiffness: 50, damping: 10, delay: 0.2 }}
                    >
                        <div ref={chatWindowRef} className={`flex-1 overflow-y-auto flex flex-col gap-2 mb-4 p-2 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-thin ${isDarkTheme ? 'scrollbar-thumb-gray-600 scrollbar-track-gray-700' : 'scrollbar-thumb-pink-300 scrollbar-track-pink-100'}`}>
                            {messages.map((msg, idx) => (
                                <motion.div
                                    key={idx}
                                    className={`flex items-end text-lg p-3 rounded-2xl max-w-[80%] break-words shadow-md ${msg.type === "bot" ? (isDarkTheme ? 'bg-gray-700 text-white self-start' : 'bg-pink-100 text-black self-start') : (isDarkTheme ? 'bg-pink-600 text-white self-end' : 'bg-pink-400 text-white self-end')}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {msg.type === "bot" && <span className="mr-2 text-2xl">🐱</span>}
                                    {msg.type === "user" && <span className="mr-2 text-2xl">🧑</span>}
                                    <span>{msg.text}</span>
                                </motion.div>
                            ))}
                        </div>

                        {voiceError && (
                            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                                className={`text-sm mb-2 p-2 rounded-lg text-center ${isDarkTheme ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-600'}`}>
                                {voiceError}
                            </motion.div>
                        )}

                        {status !== "done" && (
                            <div className="flex gap-2 items-center px-2 sm:px-0">
                                <TextField
                                    inputRef={inputRef}
                                    type="text"
                                    value={userInput}
                                    onChange={(e) => setUserInput(e.target.value)}
                                    placeholder="Type your answer…"
                                    onKeyDown={e => e.key === "Enter" && handleSend()}
                                    disabled
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: isDarkTheme ? '#4a5568' : '#ffebee',
                                            '& input': { color: isDarkTheme ? '#e2e8f0' : '#2d3748' },
                                        },
                                    }}
                                />
                                <MuiButton
                                    variant="contained"
                                    onClick={handleVoice}
                                    disabled={status === "correct"}
                                    className={`${isDarkTheme ? '!bg-pink-600 hover:!bg-pink-700' : '!bg-pink-400 hover:!bg-pink-500'} !text-white !min-w-0 !p-2 !rounded-xl !shadow-md`}
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Mic size={24} />
                                </MuiButton>
                                <MuiButton
                                    variant="contained"
                                    onClick={handleSend}
                                    disabled={status === "correct"}
                                    className={`${isDarkTheme ? '!bg-pink-600 hover:!bg-pink-700' : '!bg-pink-500 hover:!bg-pink-600'} !text-white !min-w-0 !p-2 !rounded-xl !shadow-md`}
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Send size={24} />
                                </MuiButton>
                                <AnimatePresence>
                                    {showSkip && (
                                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.2 }}>
                                            <MuiButton
                                                variant="outlined"
                                                onClick={goNext}
                                                className={`${isDarkTheme ? '!bg-gray-700 !text-pink-300 hover:!bg-gray-600' : '!bg-pink-100 !text-pink-600 hover:!bg-pink-200'} !font-bold !py-2 !px-4 !rounded-xl !shadow-md`}
                                                component={motion.button}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                Skip
                                            </MuiButton>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}
                    </motion.div>

                    <div className="hidden md:block w-full max-w-sm">
                        <Typography variant="body2" className={`text-center mb-2 ${isDarkTheme ? 'text-white' : 'text-black'}`}>
                            Use this keyboard to type your response:
                        </Typography>
                        <JapaneseKeyboard
                            onKeyPress={handleKeyboardKeyPress}
                            onBackspace={handleKeyboardBackspace}
                            onEnter={handleKeyboardEnter}
                            isDarkTheme={isDarkTheme}
                        />
                    </div>
                </div>
            </main>
        </div>
    );
}
